
import { tables, supabase } from '@/lib/supabase';
import { StaffLocation } from '@/types/staffLocationTypes';
import { Location } from '@/types/locationTypes';
import { StaffMember } from '@/types/staffTypes';
import { handleError, ErrorSeverity } from '@/utils/errorHandler';

// Helper function to convert generic data to StaffLocation type
const toStaffLocation = (data: any): StaffLocation => ({
  id: data.id,
  staff_id: data.staff_id,
  location_id: data.location_id,
  organization_id: data.organization_id,
  assigned_at: data.assigned_at
});

// Helper function to convert generic data to Location type
const toLocation = (data: any): Location => ({
  id: data.id,
  organization_id: data.organization_id,
  name: data.name,
  address: data.address,
  city: data.city,
  state: data.state,
  zip: data.zip,
  type: data.type,
  notes: data.notes
});

// Helper function to convert generic data to StaffMember type
const toStaffMember = (data: any): StaffMember => ({
  id: data.id,
  organization_id: data.organization_id,
  first_name: data.first_name,
  last_name: data.last_name,
  email: data.email || null,
  phone: data.phone || null,
  role: data.role || 'instructor',
  profile_photo_url: data.profile_photo_url || null,
  is_active: typeof data.is_active === 'boolean' ? data.is_active : true
});

export const getStaffLocations = async (staffId: string): Promise<StaffLocation[]> => {
  try {
    const { data, error } = await tables.from('staff_locations')
      .select('*')
      .eq('staff_id', staffId);

    if (error) throw error;
    return data ? data.map(toStaffLocation) : [];
  } catch (error) {
    handleError(
      error,
      "Failed to fetch staff locations",
      ErrorSeverity.ERROR,
      { operation: 'getStaffLocations', additionalInfo: { staffId } }
    );
    return [];
  }
};

export const assignStaffToLocation = async (
  staffId: string,
  locationId: string,
  organizationId: string
): Promise<StaffLocation | null> => {
  try {
    const { data, error } = await tables.from('staff_locations')
      .insert([
        {
          staff_id: staffId,
          location_id: locationId,
          organization_id: organizationId
        }
      ])
      .select();

    if (error) throw error;
    return data && data.length > 0 ? toStaffLocation(data[0]) : null;
  } catch (error) {
    handleError(
      error,
      "Failed to assign staff to location",
      ErrorSeverity.ERROR,
      { operation: 'assignStaffToLocation', additionalInfo: { staffId, locationId } }
    );
    return null;
  }
};

export const removeStaffFromLocation = async (
  staffId: string,
  locationId: string
): Promise<boolean> => {
  try {
    const { error } = await tables.from('staff_locations')
      .delete()
      .eq('staff_id', staffId)
      .eq('location_id', locationId);

    if (error) throw error;
    return true;
  } catch (error) {
    handleError(
      error,
      "Failed to remove staff from location",
      ErrorSeverity.ERROR,
      { operation: 'removeStaffFromLocation', additionalInfo: { staffId, locationId } }
    );
    return false;
  }
};

export const getLocationsWithStaffAssignments = async (
  staffId: string,
  organizationId: string
): Promise<{ locations: Location[]; assignedLocationIds: string[] }> => {
  try {
    // Get all locations for the organization
    const { data: locationsData, error: locationsError } = await tables.from('locations')
      .select('*')
      .eq('organization_id', organizationId);

    if (locationsError) throw locationsError;

    // Get all location assignments for the staff member
    const { data: staffLocationsData, error: staffLocationsError } = await tables.from('staff_locations')
      .select('location_id')
      .eq('staff_id', staffId);

    if (staffLocationsError) throw staffLocationsError;

    // Convert data to proper types and extract assigned location IDs
    const locations = locationsData ? locationsData.map(toLocation) : [];
    
    // Process staff locations data safely
    const assignedLocationIds: string[] = [];
    
    if (staffLocationsData && Array.isArray(staffLocationsData)) {
      // Use for loop with explicit null checks for each item
      for (let i = 0; i < staffLocationsData.length; i++) {
        const itemAtIndex = staffLocationsData[i];
        // Skip if item is null or not an object
        if (itemAtIndex === null || typeof itemAtIndex !== 'object') continue;
        
        // Use a non-null assertion after the check to make TypeScript happy
        const item = itemAtIndex!;
        
        // Check if location_id exists and is a string
        if ('location_id' in item && 
            typeof item.location_id === 'string' && 
            item.location_id !== '') {
          assignedLocationIds.push(item.location_id);
        }
      }
    }

    return {
      locations,
      assignedLocationIds
    };
  } catch (error) {
    handleError(
      error,
      "Failed to get locations with staff assignments",
      ErrorSeverity.ERROR,
      { operation: 'getLocationsWithStaffAssignments', additionalInfo: { staffId } }
    );
    return { locations: [], assignedLocationIds: [] };
  }
};

/**
 * Get staff members assigned to a specific location
 * @param locationId The ID of the location to filter by
 * @param organizationId The organization ID
 * @returns An array of staff members assigned to the specified location
 */
export const getStaffByLocation = async (
  locationId: string,
  organizationId: string
): Promise<StaffMember[]> => {
  try {
    // Query staff members with location assignments using a join
    const { data, error } = await supabase
      .from('staff_members')
      .select(`
        *,
        staff_locations!inner(location_id)
      `)
      .eq('staff_locations.location_id', locationId)
      .eq('organization_id', organizationId);

    if (error) throw error;
    
    // Map results to StaffMember type
    return data ? data.map(toStaffMember) : [];
  } catch (error) {
    handleError(
      error,
      "Failed to fetch staff by location",
      ErrorSeverity.ERROR,
      { operation: 'getStaffByLocation', additionalInfo: { locationId } }
    );
    return [];
  }
};
